import { useEffect, useMemo, useState } from 'react'
import { Button, Col, Container, Dropdown, Form, Row } from 'react-bootstrap'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useNavigate, useParams } from 'react-router-dom'
import countryList from 'react-select-country-list'
import KLayerNavigation from '../components/KLayerNavigation'
import { formatDDMMYYYY, hideKAlert, isUserLoggedIn, parseDDMMYYYY } from '../helpers'
import { getLocale, t } from '../locale'
import { Client } from '../react-app-env'
import { createClient, fetchClientById, updateClient } from '../service/client'
import v from '../validators'
import { CountryFlag } from '../components/CountryFlag'

function ClientForm(props: any) {
  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      if ((event.target as HTMLElement).closest('.date-picker-wrapper')) return
      setShowDatePicker(false)
    }

    document.addEventListener('mouseup', closeOnOutsideClick)
    document.addEventListener('touchend', closeOnOutsideClick)
    return () => {
      document.removeEventListener('mouseup', closeOnOutsideClick)
      document.removeEventListener('touchend', closeOnOutsideClick)
    }
  }, [])

  const navigate = useNavigate()

  const params: any = useParams()
  const clientId = params.clientId

  const options = useMemo(() => {
    const displayNames = new Intl.DisplayNames([getLocale()], { type: 'region' })
    const priority = ['DE', 'CH', 'AT', 'FR', 'IT', 'ES']
    const all = countryList()
      .getData()
      .map(c => ({ value: c.value, label: displayNames.of(c.value) ?? c.label }))
      .sort((a, b) => a.label.localeCompare(b.label, getLocale()))
    const priorityOptions = priority
      .map(code => all.find(o => o.value === code))
      .filter((o): o is { value: string; label: string } => !!o)
    const restOptions = all.filter(o => !priority.includes(o.value))
    return [...priorityOptions, ...restOptions]
  }, [])

  const [client, setClient] = useState({} as Client)

  const [email, setEmail] = useState('')
  const [gender, setGender] = useState('')
  const [isPerson, setIsPerson] = useState(0)
  const [name, setName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [country, setCountry] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [phone, setPhone] = useState('')
  const [clientNumber, setClientNumber] = useState('')
  const [uid, setUid] = useState('')

  const [streetAddress, setStreetAddress] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [city, setCity] = useState('')
  const [contacts, setContacts] = useState<{ name: string; email: string; phone: string }[]>([])
  const [showAddress, setShowAddress] = useState(false)
  const [showContacts, setShowContacts] = useState(false)

  const [isCompany, setIsCompany] = useState(true)

  const [isNameValid, setIsNameValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isCountryValid, setIsCountryValid] = useState(true)
  const [isEmailValid, setIsEmailValid] = useState(true) // state-ovi koje sam ja dodao
  const [isGenderValid, setIsGenderValid] = useState(true)
  const [isDateOfBirthValid, setIsDateOfBirthValid] = useState(true)
  const [isIsPersonValid, setIsIsPersonValid] = useState(true)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isClientNumberValid, setIsClientNumberValid] = useState(true)

  function create(clientData: any) {
    createClient(clientData).then(r => {
      if (r.ok) {
        navigate(`/clients?clientStatus=${true}&&deleted=empty`)
      }
    })
  }

  function update(clientData: any) {
    updateClient(clientData).then(r => {
      if (r.ok) {
        navigate(`/clients?clientStatus=${true}&&deleted=empty`)
      }
    })
  }

  useEffect(() => {
    props.edit &&
      fetchClientById(clientId).then(r => {
        if (!!r.ok) {
          const client = r.client as Client
          setClient(client)

          setEmail(client.email)
          setGender(client.gender)
          setIsPerson(client.isPerson)
          setIsCompany(client.isPerson !== 1)
          setName(client.name)
          setFirstName(client.firstName)
          setCountry(client.country)
          setDateOfBirth(client.dateOfBirth)
          setPhone(client.phoneNumber)
          setClientNumber(client.clientNumber.toString())
          setUid(client.uid || '')

          setStreetAddress(client.streetAddress || '')
          setZipCode(client.zipCode || '')
          setCity(client.city || '')
          setContacts(
            (client.contacts || []).map(contact => ({
              name: contact.name || '',
              email: contact.email || '',
              phone: contact.phone || '',
            }))
          )
          if (client.streetAddress || client.zipCode || client.city) setShowAddress(true)
          if (client.contacts && client.contacts.length > 0) setShowContacts(true)
        }
      })
  }, [clientId, props])

  function addContact() {
    setContacts(prev => [...prev, { name: '', email: '', phone: '' }])
  }

  function removeContact(index: number) {
    setContacts(prev => prev.filter((_, i) => i !== index))
  }

  function handleContactChange(index: number, field: 'name' | 'email' | 'phone', value: string) {
    setContacts(prev =>
      prev.map((contact, i) => (i === index ? { ...contact, [field]: value } : contact))
    )
  }

  function handleEmail(e: any) {
    const value = e.target.value.trimStart()
    setEmail(value)
    setIsEmailValid(value.trim() === '' || v.email(value))
  }

  function handleGender(e: any) {
    setGender(e.target.value)
    setIsGenderValid(v.gender(e.target.value))
  }

  function handleIsPerson(e: any) {
    const value = Number(e.target.value)
    setIsPerson(value)
    setIsCompany(value !== 1)
    setIsIsPersonValid(v.isPerson(value))
  }
  function handleName(e: any) {
    setName(e.target.value.trimStart())
    setIsNameValid(v.companyName(name))
  }
  function handleFirstName(e: any) {
    setFirstName(e.target.value.trimStart())
    setIsFirstNameValid(v.name(firstName))
  }
  function handleCountry(value: string) {
    setCountry(value)
    setIsCountryValid(value.trim() === '' || v.name(value))
  }
  function handleDateOfBirth(date: Date | undefined) {
    if (date) {
      const formattedDate = formatDDMMYYYY(date)
      setDateOfBirth(formattedDate)
      setIsDateOfBirthValid(v.dateOfBirth(formattedDate))
      setShowDatePicker(false)
    } else {
      setDateOfBirth('')
      setIsDateOfBirthValid(true)
    }
  }

  function handleDateOfBirthInput(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value

    setDateOfBirth(value)

    setIsDateOfBirthValid(value.trim() === '' || v.dateOfBirth(value))
  }

  function handlerRegisternummber(e: any) {
    setClientNumber(e.target.value.trim())
  }

  function handleSubmit(e: any) {
    e.preventDefault()

    const emailValid = email.trim() === '' || v.email(email)
    const countryValid = country.trim() === '' || v.name(country)
    const dateOfBirthValid = isCompany || dateOfBirth.trim() === '' || v.dateOfBirth(dateOfBirth)

    setIsEmailValid(emailValid)
    if (!isCompany) {
      setIsGenderValid(v.gender(gender))
      setIsFirstNameValid(v.name(firstName))
      setIsDateOfBirthValid(dateOfBirthValid)
    }
    setIsIsPersonValid(v.isPerson(isPerson))
    setIsNameValid(v.companyName(name))
    setIsCountryValid(countryValid)
    setIsClientNumberValid(v.clientNumber(clientNumber))

    if (
      !emailValid ||
      !v.isPerson(isPerson) ||
      !v.name(name) ||
      !v.clientNumber(clientNumber) ||
      (!isCompany && (!v.name(firstName) || !dateOfBirthValid || !v.gender(gender)))
    ) {
      return
    }

    const clientData = {
      clientId: client._id,
      email: email.trim(),
      gender: gender,
      isPerson: isPerson,
      name: name.trim(),
      firstName: firstName?.trim(),
      country: country.trim(),
      dateOfBirth: dateOfBirth.trim(),
      clientNumber: Number(clientNumber),
      phoneNumber: phone,
      ...(uid.trim() ? { uid: uid.trim() } : {}),
      ...(streetAddress.trim() ? { streetAddress: streetAddress.trim() } : {}),
      ...(zipCode.trim() ? { zipCode: zipCode.trim() } : {}),
      ...(city.trim() ? { city: city.trim() } : {}),
      ...(() => {
        const filledContacts = contacts
          .filter(contact => contact.name.trim() || contact.email.trim() || contact.phone.trim())
          .map(contact => ({
            name: contact.name.trim(),
            email: contact.email.trim(),
            phone: contact.phone.trim(),
          }))
        return filledContacts.length ? { contacts: filledContacts } : {}
      })(),
    }

    props.edit ? update(clientData) : create(clientData)
  }
  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>

        <main className='col-md-9 ms-sm-auto col-lg-10 px-md-4 pb-5'>
          <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
            <a className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {props.edit ? t('editClientData') : t('createNewClient')}
            </a>
          </div>

          <div className='col-12'>
            <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
              <Row>
                <Col lg={10} xl={9} className='mx-auto'>
                  <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-md'>
                    <Row className='g-4'>
                      {/* Natural person */}
                      <Col md={12}>
                        <p className='fw-medium smaller text-primary mb-1'>{t('naturalPerson')}</p>
                        <Form.Select
                          id='natural_person'
                          value={isPerson}
                          onChange={handleIsPerson}
                          isInvalid={!isIsPersonValid}
                          required
                        >
                          <option value=''>--</option>
                          <option value={1}>{t('yes')}</option>
                          <option value={0}>{t('no')}</option>
                        </Form.Select>
                      </Col>

                      {/* Gender (if person) */}
                      {!isCompany && (
                        <Col md={12}>
                          <p className='fw-medium smaller text-primary mb-1'>{t('gender')}</p>
                          <Form.Select
                            id='gender'
                            value={gender}
                            onChange={handleGender}
                            isInvalid={!isGenderValid}
                            required
                          >
                            <option value=''>--</option>
                            <option value='M'>{t('M')}</option>
                            <option value='F'>{t('F')}</option>
                          </Form.Select>
                        </Col>
                      )}

                      {/* First name */}
                      {!isCompany && (
                        <Col md={6}>
                          <p className='fw-medium smaller text-primary mb-1'>{t('firstName')}</p>
                          <Form.Control
                            id='firstName'
                            type='text'
                            value={firstName}
                            onChange={handleFirstName}
                            onKeyUp={handleFirstName}
                            isInvalid={!isFirstNameValid}
                          />
                        </Col>
                      )}

                      {/* Last name / Company */}
                      <Col md={isCompany ? 12 : 6}>
                        <p className='fw-medium smaller text-primary mb-1'>
                          {isCompany ? t('companyName') : t('lastName')}
                        </p>
                        <Form.Control
                          id='lastName'
                          type='text'
                          value={name}
                          onChange={handleName}
                          onKeyUp={handleName}
                          isInvalid={!isNameValid}
                        />
                      </Col>
                      {/* Date of birth */}
                      {!isCompany && (
                        <Col md={6} style={{ position: 'relative' }}>
                          <p className='fw-medium smaller text-primary mb-1'>{t('dateOfBirth')}</p>
                          <Form.Control
                            type='text'
                            value={dateOfBirth}
                            onChange={handleDateOfBirthInput}
                            placeholder='DD.MM.YYYY'
                            inputMode='numeric'
                            maxLength={10}
                            isInvalid={!isDateOfBirthValid}
                            onClick={() => setShowDatePicker(true)}
                          />
                          {showDatePicker && (
                            <div
                              style={{
                                position: 'absolute',
                                zIndex: 1000,
                                backgroundColor: 'white',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px',
                                padding: '10px',
                                marginTop: '5px',
                              }}
                            >
                              <DayPicker
                                mode='single'
                                selected={parseDDMMYYYY(dateOfBirth) ?? undefined}
                                month={parseDDMMYYYY(dateOfBirth) ?? undefined}
                                onSelect={(d: Date | undefined) => {
                                  handleDateOfBirth(d)
                                  setShowDatePicker(false)
                                }}
                                captionLayout='dropdown'
                                startMonth={new Date(1900, 0)}
                                endMonth={new Date()}
                              />
                            </div>
                          )}
                        </Col>
                      )}

                      {/* Country */}
                      <Col md={12}>
                        <p className='fw-medium smaller text-primary mb-1'>{t('country')}</p>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant='outline-secondary'
                            className={`w-100 d-flex align-items-center form-control text-start ${!isCountryValid ? 'is-invalid border-danger' : ''}`}
                          >
                            {country && <CountryFlag code={country} />}
                            <span className='ms-2'>
                              {options.find(o => o.value === country)?.label ?? '--'}
                            </span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
                            {options.map(option => (
                              <Dropdown.Item
                                key={option.value}
                                active={option.value === country}
                                onClick={() => handleCountry(option.value)}
                                className='d-flex align-items-center'
                              >
                                <CountryFlag code={option.value} />
                                <span className='ms-2'>{option.label}</span>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Col>

                      {/* Separator */}
                      <Col xs={12}>
                        <hr className='my-3' style={{ opacity: 0.25 }} />
                      </Col>

                      {/* Email */}
                      <Col md={12}>
                        <p className='fw-medium smaller text-primary mb-1'>{t('emailAddress')}</p>
                        <Form.Control
                          id='email'
                          type='email'
                          value={email}
                          onChange={handleEmail}
                          onKeyUp={handleEmail}
                          isInvalid={!isEmailValid}
                        />
                      </Col>

                      {/* Phone */}
                      <Col md={12}>
                        <p className='fw-medium smaller text-primary mb-1'>{t('phone')}</p>
                        <PhoneInput
                          inputClass='form-control bg-white rounded-md w-100'
                          buttonClass='bg-white'
                          country={window.location.hostname.endsWith('.ch') ? 'ch' : 'de'}
                          value={phone}
                          onChange={phone => setPhone(phone)}
                          inputStyle={{ width: '100%' }}
                          enableSearch={true}
                          preferredCountries={['de', 'ch', 'at']}
                        />
                      </Col>

                      {/* Separator */}
                      <Col xs={12}>
                        <hr className='my-3' style={{ opacity: 0.25 }} />
                      </Col>

                      {/* Registernummer */}
                      <Col md={12}>
                        <p className='fw-medium smaller text-primary mb-1'>{t('registerNumber')}</p>
                        <Form.Control
                          id='registernummer'
                          type='text'
                          value={clientNumber}
                          onChange={handlerRegisternummber}
                          isInvalid={!isClientNumberValid}
                          onKeyUp={handlerRegisternummber}
                          required
                        />
                      </Col>

                      {/* UID (optional, companies only) */}
                      {isCompany && (
                        <Col md={12}>
                          <p className='fw-medium smaller text-primary mb-1'>
                            {t('transparenzChCompanyUid')}
                            <span className='text-muted fw-normal ms-1'>
                              ({t('optional') || 'optional'})
                            </span>
                          </p>
                          <Form.Control
                            id='uid'
                            type='text'
                            value={uid}
                            placeholder={
                              window.location.hostname.endsWith('.ch')
                                ? 'CHE-123.456.789'
                                : 'DE-123456789'
                            }
                            onChange={e => setUid(e.target.value.trim())}
                          />
                        </Col>
                      )}

                      {/* Separator */}
                      <Col xs={12}>
                        <hr className='my-3' style={{ opacity: 0.25 }} />
                      </Col>

                      {/* Address (optional, collapsed by default) */}
                      <Col xs={12}>
                        <p
                          className='fw-medium smaller text-primary mb-1'
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowAddress(prev => !prev)}
                        >
                          <i className={`bi bi-chevron-${showAddress ? 'down' : 'right'} me-1`} />
                          {t('address')}
                          <span className='text-muted fw-normal ms-1'>({t('optional')})</span>
                        </p>
                      </Col>

                      {showAddress && (
                        <>
                          <Col md={12}>
                            <p className='fw-medium smaller text-primary mb-1'>
                              {t('streetAddress')}
                            </p>
                            <Form.Control
                              id='streetAddress'
                              type='text'
                              value={streetAddress}
                              onChange={e => setStreetAddress(e.target.value)}
                            />
                          </Col>
                          <Col md={6}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('zipCode')}</p>
                            <Form.Control
                              id='zipCode'
                              type='text'
                              value={zipCode}
                              onChange={e => setZipCode(e.target.value)}
                            />
                          </Col>
                          <Col md={6}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('city')}</p>
                            <Form.Control
                              id='city'
                              type='text'
                              value={city}
                              onChange={e => setCity(e.target.value)}
                            />
                          </Col>
                        </>
                      )}

                      {/* Separator */}
                      <Col xs={12}>
                        <hr className='my-3' style={{ opacity: 0.25 }} />
                      </Col>

                      {/* Contacts (optional, collapsed by default) */}
                      <Col xs={12}>
                        <p
                          className='fw-medium smaller text-primary mb-1'
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowContacts(prev => !prev)}
                        >
                          <i className={`bi bi-chevron-${showContacts ? 'down' : 'right'} me-1`} />
                          {t('contacts')}
                          <span className='text-muted fw-normal ms-1'>({t('optional')})</span>
                        </p>
                      </Col>

                      {showContacts && (
                        <Col xs={12}>
                          {contacts.map((contact, index) => (
                            <Row className='g-2 mb-2' key={index}>
                              <Col md={4}>
                                <Form.Control
                                  type='text'
                                  placeholder={t('name')}
                                  value={contact.name}
                                  onChange={e => handleContactChange(index, 'name', e.target.value)}
                                />
                              </Col>
                              <Col md={4}>
                                <Form.Control
                                  type='email'
                                  placeholder={t('email')}
                                  value={contact.email}
                                  onChange={e =>
                                    handleContactChange(index, 'email', e.target.value)
                                  }
                                />
                              </Col>
                              <Col md={3}>
                                <Form.Control
                                  type='text'
                                  placeholder={t('phone')}
                                  value={contact.phone}
                                  onChange={e =>
                                    handleContactChange(index, 'phone', e.target.value)
                                  }
                                />
                              </Col>
                              <Col md={1} className='d-flex align-items-center'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => removeContact(index)}
                                >
                                  <i className='bi bi-trash' />
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button variant='outline-secondary' size='sm' onClick={addContact}>
                            <i className='bi bi-plus-lg me-1' />
                            {t('addContact')}
                          </Button>
                        </Col>
                      )}

                      {/* Separator */}
                      <Col xs={12}>
                        <hr className='my-3' style={{ opacity: 0.25 }} />
                      </Col>

                      {/*Buttons*/}
                      <Col xs={6} className='text-start'>
                        <Button
                          onClick={() => {
                            hideKAlert()
                            navigate(`/clients?clientStatus=${true}&&deleted=empty`)
                          }}
                          className='btn-warning text-white'
                        >
                          « {t('cancel')}
                        </Button>
                      </Col>

                      <Col xs={6} className='text-end'>
                        <Button
                          onClick={handleSubmit}
                          className='btn-success text-white'
                          type='submit'
                        >
                          {props.edit ? t('save') : t('create')} »
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </main>
      </Row>
    </Container>
  )
}

export default ClientForm
